import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ProjectOpportunityService } from '../../../services/project-opportunity';
import { ColumnBoxFlexFullWidth, FlexBoxFullWidth } from '../../common/boxes';
import { ChangeEvent, useCallback, useState } from 'react';
import { Box, Button, IconButton, MenuItem, TextField, Tooltip } from '@mui/material';
import { TEXTS } from '../../../core/texts';
import { ValidationErrorState } from '../../../types/validationErrorState';
import { ShadowContainer } from '../../common/ShadowContainer';
import { BorderBoxWithTitle } from '../../common/BorderBoxWithTitle';
import { Edit } from '@mui/icons-material';
import { ButtonWithLoader } from '../../common/ButtonWithLoader';
import { IProjectOpportunity } from '../../../interfaces/projectOpportunity';
import { Validator } from '../../../core/Validator';
import NAMES = Validator.NAMES;
import { AxiosError } from 'axios';
import { IErrorResponse } from '../../../interfaces/errorResponse';
import { parseAxiosError } from '../../../services/axios';
import { GET_PROJECT_DETAILS_QUERY_KEY } from '../../../queryHooks/project/useGetProjectOpportunityDetails';
import { useNotification } from '../../../+state/useNotification';

const ProjectOpportunityDetails = ({ clientId, project }: { clientId: number, project: IProjectOpportunity }) => {
  const { openErrorToast, openSuccessToast } = useNotification();
  const queryClient = useQueryClient();

  const [detailsEditMode, setDetailsEditMode] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorState>({});
  const [details, setDetails] = useState({ title: project.title, overview: project.overview });

  const toggleDetailsEdit = useCallback(() => setDetailsEditMode(prev => {
    if (prev) {
      setDetails({ title: project.title, overview: project.overview });
    }
    return !prev;
  }), [project]);

  const mutationDetails = useMutation({
    mutationFn: (data: { title: string, overview: string }) => ProjectOpportunityService.updateProjectDetails({
      clientId,
      projectOpportunityId: project.id,
      data
    }),
    onSuccess: (data) => {
      setDetails({ title: data.title, overview: data.overview });
      queryClient.invalidateQueries([GET_PROJECT_DETAILS_QUERY_KEY]);
      openSuccessToast(TEXTS.SUCCESS_SAVED);
      setDetailsEditMode(false);
    },
    onError: (err: AxiosError<IErrorResponse>) => openErrorToast(parseAxiosError(err))
  });

  const handleDetailsChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDetails(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleDetailsFormSubmit = useCallback((e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (details.title === project.title && details.overview === project.overview) {
      return toggleDetailsEdit();
    }
    const validErrors = Validator.validate(details, NAMES.PROJECT);
    if (validErrors) {
      return setValidationErrors(validErrors);
    }
    mutationDetails.mutate(details);
  }, [mutationDetails, project.overview, project.title, toggleDetailsEdit, details]);

  return (
    <ShadowContainer position={'relative'}>
      <ColumnBoxFlexFullWidth maxWidth={1200} alignSelf={'center'}>
        <BorderBoxWithTitle title={TEXTS.PROJECT_DETAILS} mb={3}>
          <form onSubmit={handleDetailsFormSubmit}>
            <ColumnBoxFlexFullWidth justifyContent={'space-between'}>
              <TextField value={detailsEditMode ? details.title : project.title} onChange={handleDetailsChange}
                         label={TEXTS.TITLE}
                         name={'title'}
                         error={!!validationErrors.title} helperText={validationErrors.title}
                         variant={'standard'} sx={{ mb: 3 }} disabled={!detailsEditMode}
              />
              <TextField value={detailsEditMode ? details.overview : project.overview} onChange={handleDetailsChange}
                         label={TEXTS.OVERVIEW}
                         name={'overview'}
                         error={!!validationErrors.overview} helperText={validationErrors.overview}
                         variant={'standard'} multiline rows={3} sx={{ mb: 3 }} disabled={!detailsEditMode}
              />
              {
                detailsEditMode
                  ? <FlexBoxFullWidth justifyContent={'space-between'}>
                    <Button onClick={toggleDetailsEdit} color={'error'} variant={'outlined'}>{TEXTS.CANCEL}</Button>
                    <ButtonWithLoader loading={mutationDetails.isLoading} variant={'outlined'} type={'submit'}>
                      {TEXTS.SAVE}
                    </ButtonWithLoader>
                  </FlexBoxFullWidth>
                  :
                  <Box alignSelf={'flex-end'}>
                    <Tooltip title={'Edit'}>
                      <IconButton color={'primary'} onClick={toggleDetailsEdit}>
                        <Edit/>
                      </IconButton>
                    </Tooltip>
                  </Box>

              }
            </ColumnBoxFlexFullWidth>
          </form>
        </BorderBoxWithTitle>
      </ColumnBoxFlexFullWidth>
    </ShadowContainer>
  );
};

export default ProjectOpportunityDetails;